// apps/web/src/lib/textSize.ts
// Dynamic Type support (UI plan §5). On iOS, index.css gives html.ios-native
// `font: -apple-system-body`, so the root font-size follows the user's system
// text-size setting. On top of that sits an in-app zoom multiplier for users
// who don't know the iOS setting exists (most of the target audience).
//
// The multiplier can't be pure CSS: a percentage on :root resolves against
// the initial 16px and would lose the system-scaled value. So we clear any
// inline size, read what the system gave us, then set base × zoom in px.
// CalolyViewController re-dispatches "caloly:textsize" whenever the user
// changes the system setting while the app is running.

const ZOOM_KEY = "caloly.text_zoom";

export const TEXT_ZOOMS = [0.9, 1.0, 1.15, 1.3] as const;
export type TextZoom = (typeof TEXT_ZOOMS)[number];

export function getTextZoom(): TextZoom {
  try {
    const v = Number(localStorage.getItem(ZOOM_KEY));
    return (TEXT_ZOOMS as readonly number[]).includes(v) ? (v as TextZoom) : 1.0;
  } catch {
    return 1.0;
  }
}

export function setTextZoom(zoom: TextZoom): void {
  try { localStorage.setItem(ZOOM_KEY, String(zoom)); } catch { /* ignore */ }
  applyTextSize();
}

/** Recompute root font-size = system base × user zoom. Idempotent. */
export function applyTextSize(): void {
  const root = document.documentElement;
  root.style.fontSize = ""; // fall back to the system-scaled value first
  const base = parseFloat(getComputedStyle(root).fontSize) || 16;
  const zoom = getTextZoom();
  if (zoom !== 1.0) root.style.fontSize = `${base * zoom}px`;
}

/** Boot hook: apply once and follow live system text-size changes (iOS). */
export function startTextSize(): void {
  applyTextSize();
  window.addEventListener("caloly:textsize", applyTextSize);
}
